import type { GetStaticPaths, GetStaticPropsContext } from 'next'

import { FileTopic } from '~/types/theory'
import DrillTopicPage from '~/drill/pages/DrillTopicPage.page'

import { AppLanguage } from '~/types/locale'
import { DEFAULT_LAYOUT_TRANSLATION_MODULE_NAMES } from '~/shared/layouts/constants'
import { getTopicData, isFile } from '~/../utils/data-fetch/theory/theory.query'
import { TexLayout } from '~/shared/layouts/TexLayout'

import { getMultipleChoiceTest, getRunningQueriesThunk } from '~/redux/apis/multiple-choice-test-api'

import { getStaticPropsWrapper } from '~/utils/pageWrapper/staticPageWrapper'
import { htmlToPlainText } from '~/utils/htmlToPlainText'
import { GetProps } from '~/utils/pageWrapper/types'

export default function DrillTopicPageWrapper({
  articleBody,
  topic,
}: {
  articleBody: string
  topic: FileTopic
}): React.ReactElement {
  return <DrillTopicPage articleBody={articleBody} topic={topic} />
}

DrillTopicPageWrapper.getLayout = TexLayout

export const getStaticPaths: GetStaticPaths = () => {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export const getProps: GetProps =
  (store) =>
  async ({ locale, params }: GetStaticPropsContext<{ topicId: string; pageId: string }>) => {
    const { topicId, pageId } = params || {}
    if (!topicId || !pageId) {
      return { notFound: true }
    }
    const drillPaths = ['drill', topicId, pageId]

    const topicData = await getTopicData({
      paths: drillPaths,
      language: locale as AppLanguage,
    })

    if (topicData && 'destinationPath' in topicData) {
      return {
        redirect: {
          destination: topicData.destinationPath,
          statusCode: 308,
        },
      }
    } else if (!topicData || !isFile(topicData.topic)) {
      return { notFound: true }
    }

    const { topic } = topicData

    store.dispatch(
      getMultipleChoiceTest.initiate({
        url: topic.path,
      }),
    )

    await Promise.all(store.dispatch(getRunningQueriesThunk()))

    const articleBody = htmlToPlainText(topic.htmlContent.html)

    return {
      props: {
        articleBody,
        path: `/drill/${topicId}/${pageId}`,
        topic,
        namespaces: [
          ...DEFAULT_LAYOUT_TRANSLATION_MODULE_NAMES,
          'encyclopedia',
          'multipleChoice',
          'drill',
          'quizSchema',
        ],
      },
    }
  }

export const getStaticProps = getStaticPropsWrapper(getProps)
